import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@core/contexts/AuthContext";
import { UsageAnalyticsTracker } from "@core/services/UsageAnalyticsTracker";
import { useGroups } from "@core/hooks/useGroups";
import { useToast } from "@core/hooks/use-toast";
import { useFreemiumQuota } from "@core/hooks/useFreemiumQuota";
import { toIntlLocale } from "@core/lib/date-utils";
import {
  Users,
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Search,
  Mail,
  Calendar,
  BarChart3,
  Shield,
  Lock,
} from "lucide-react";
import { Button } from "@core/components/ui/button";
import { Input } from "@core/components/ui/input";
import { GroupList } from "@core/components/groups";
import { GroupEditor, NOUVEAU_GROUPE } from "@core/components/groups";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";

const DatePollsGroupsPage: React.FC = () => {
  const { t, i18n } = useTranslation(["polls", "common", "errors"]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const {
    groups,
    lockedCount,
    createGroup,
    updateGroup,
    deleteGroup,
    loadGroups,
    canCreate,
    loading,
  } = useGroups();
  const { toast } = useToast();
  const { status: quotaStatus } = useFreemiumQuota();
  const tracked = useRef(false);

  useEffect(() => {
    if (!canCreate && !tracked.current) {
      tracked.current = true;
      void UsageAnalyticsTracker.track("feature_gate_seen", {
        location: "groups_page",
      });
    }
  }, [canCreate]);

  const handleBack = () => {
    navigate("/dashboard");
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);

  // Filtrer les groupes selon la recherche
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groups;

    return groups.filter(
      (group) =>
        group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        group.member_emails.some((email) =>
          email.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
    );
  }, [groups, searchQuery]);

  // Statistiques
  const stats = useMemo(() => {
    const totalGroups = groups.length;
    const totalMembers = groups.reduce(
      (acc, group) => acc + group.member_emails.length,
      0,
    );
    const avgMembers =
      totalGroups > 0 ? Math.round(totalMembers / totalGroups) : 0;

    return { totalGroups, totalMembers, avgMembers };
  }, [groups]);

  // Mode test pour les tests sans authentification
  const isTestMode = location.search.includes("test=true");

  // Vérification d'accès premium (groupes = fonctionnalité payante)
  const hasGroupAccess = useMemo(() => {
    // Mode test : toujours autoriser
    if (isTestMode) return true;

    // Utilisateur non connecté : pas d'accès
    if (!user) return false;

    // Décision du 18/09 : la PAGE s'ouvre à tout compte, parce que le Gratuit
    // a droit à un groupe. Ce qui reste réservé, c'est la CRÉATION au-delà de
    // ce droit — arbitrée par le trigger serveur, anticipée par `canCreate`.
    // Avant, la page entière était derrière le mur « Fonctionnalité Premium »,
    // et un compte gratuit ne pouvait donc ni créer ni gérer son groupe offert.
    return true;
  }, [user, isTestMode]);

  // Vérification d'accès
  if (!hasGroupAccess) {
    return (
      <div
        className="min-h-screen-safe bg-background flex items-center justify-center"
        data-testid="access-restricted-page"
      >
        <div className="max-w-md mx-auto px-6 text-center">
          <Shield className="w-16 h-16 text-warning mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-4">
            {isTestMode
              ? t("polls:groups_test_mode")
              : t("polls:groups_premium_feature")}
          </h1>
          <p className="text-muted-foreground mb-8">
            {isTestMode
              ? t("polls:groups_test_mode_access")
              : t("polls:groups_premium_restricted")}
          </p>
          {!isTestMode && (
            <div className="mb-6">
              <p className="text-foreground mb-4">
                {t("polls:groups_premium_upgrade_prompt")}
              </p>
              <Button
                onClick={() => navigate("/pricing")}
                className="mr-3"
                data-testid="go-to-pricing-button"
              >
                {t("polls:groups_see_pricing")}
              </Button>
              <Button
                onClick={() => navigate("/dashboard")}
                className={`bg-secondary hover:bg-secondary/80 text-secondary-foreground`}
              >
                {t("polls:groups_back_to_dashboard")}
              </Button>
            </div>
          )}
          {isTestMode && (
            <div className="mb-6">
              <Button onClick={() => navigate("/dashboard")}>
                {t("polls:groups_back_to_dashboard")}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loading && groups.length === 0) {
    return (
      <div className="min-h-screen-safe bg-background flex items-center justify-center">
        <div
          className={`animate-spin rounded-full h-12 w-12 border-b-2 border-primary`}
        ></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen-safe bg-background">
      <ProductHeader productType="date" showAuth={true} />
      <div className="flex-1 p-6">
        {/* Mode test badge */}
        {isTestMode && (
          <div
            className="mb-4 bg-warning-bg border border-warning/30 rounded-lg px-4 py-2 flex items-center gap-2"
            data-testid="test-mode-badge"
          >
            <Shield className="w-4 h-4 text-warning" />
            <span className="text-warning text-sm font-medium">
              {t("polls:groups_test_mode")}
            </span>
          </div>
        )}
        <div className="max-w-6xl mx-auto">
          {/* Groupes conservés mais plus utilisables — ceux créés pendant un
              essai terminé. Sans cette ligne ils disparaissent sans un mot, et
              la personne croit les avoir perdus. Elle dit donc les deux choses
              qui comptent : ils sont là, et ce qui les rend à nouveau
              utilisables. */}
          {lockedCount > 0 && (
            <div
              className="mb-6 rounded-lg border border-warning/30 bg-warning-bg px-4 py-3 flex flex-wrap items-center justify-between gap-3"
              data-testid="groups-locked-notice"
            >
              <span className="text-sm text-foreground">
                {t("polls:groups_locked_notice", { count: lockedCount })}
              </span>
              <Button
                size="sm"
                onClick={() => navigate("/pricing")}
                data-testid="groups-locked-cta"
              >
                {t("polls:groups_see_pricing")}
              </Button>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground flex items-center gap-3 mb-2">
                  <Users className="w-8 h-8 text-primary" />
                  {t("polls:groups_title")}
                </h1>
                <p className="text-muted-foreground">
                  {t("polls:groups_subtitle")}
                </p>
                <div
                  className="flex items-center gap-6 mt-2 text-sm text-muted-foreground"
                  data-testid="groups-stats-container"
                >
                  <div
                    className="flex items-center gap-2"
                    data-testid="total-groups-stat"
                  >
                    <Users className="w-4 h-4" />
                    <span data-testid="stat-value">
                      {stats.totalGroups}
                    </span>{" "}
                    {t("polls:groups_stat_groups")}
                  </div>
                  <div
                    className="flex items-center gap-2"
                    data-testid="total-members-stat"
                  >
                    <Mail className="w-4 h-4" />
                    <span data-testid="stat-value">
                      {stats.totalMembers}
                    </span>{" "}
                    {t("polls:groups_stat_members")}
                  </div>
                  <div
                    className="flex items-center gap-2"
                    data-testid="average-members-stat"
                  >
                    <BarChart3 className="w-4 h-4" />
                    <span data-testid="stat-value">
                      {stats.avgMembers}
                    </span>{" "}
                    {t("polls:groups_stat_avg_members")}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t("polls:groups_search_placeholder")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`pl-10 bg-background border-input text-foreground placeholder:text-muted-foreground focus-visible:ring-primary`}
                    data-testid="groups-search-input"
                  />
                </div>
                {/* Quand le plan ne permet plus de créer, le bouton mène aux
                    TARIFS au lieu d'ouvrir un formulaire qui échouera à
                    l'enregistrement. Ouvrir, laisser saisir, puis refuser est
                    le pire des trois comportements possibles : la personne a
                    fait le travail pour rien, et n'apprend la règle qu'au
                    moment où elle la heurte. */}
                {canCreate ? (
                  <Button
                    onClick={() => setEditingGroupId(NOUVEAU_GROUPE)}
                    data-testid="create-group-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t("polls:groups_new_group")}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => navigate("/pricing")}
                    data-testid="create-group-locked-button"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    {t("polls:groups_create_locked")}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Contenu principal */}
          <div className="bg-card border border-border rounded-xl shadow-sm">
            {filteredGroups.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  {searchQuery
                    ? t("polls:groups_empty_search_title")
                    : t("polls:groups_empty_title")}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery
                    ? t("polls:groups_empty_search_description")
                    : t("polls:groups_empty_description")}
                </p>
                {!searchQuery && canCreate && (
                  <Button onClick={() => setEditingGroupId(NOUVEAU_GROUPE)}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t("polls:groups_create_group")}
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredGroups.map((group) => (
                  <div
                    key={group.id}
                    className="p-6 hover:bg-accent transition-colors"
                    data-testid="group-item"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3
                          className="text-lg font-semibold text-foreground mb-2"
                          data-testid="group-name"
                        >
                          {group.name}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div
                            className="flex items-center gap-1"
                            data-testid="member-count"
                          >
                            <Mail className="w-4 h-4" />
                            <span>
                              {t("polls:groups_member_count", {
                                count: group.member_emails.length,
                              })}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {t("polls:groups_created_on", {
                                date: new Date(
                                  group.created_at,
                                ).toLocaleDateString(
                                  toIntlLocale(i18n.language),
                                ),
                              })}
                            </span>
                          </div>
                        </div>

                        {/* Liste des emails (limitée pour l'affichage) */}
                        <div className="mt-3">
                          <div className="text-xs text-muted-foreground mb-1">
                            {t("polls:groups_members_label")}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {group.member_emails
                              .slice(0, 5)
                              .map((email, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded"
                                >
                                  {email}
                                </span>
                              ))}
                            {group.member_emails.length > 5 && (
                              <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                                {t("polls:groups_more_members", {
                                  count: group.member_emails.length - 5,
                                })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingGroupId(group.id)}
                          className="text-muted-foreground hover:text-foreground hover:bg-accent"
                          data-testid="edit-group-button"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (
                              confirm(
                                t("polls:groups_delete_confirm", {
                                  name: group.name,
                                }),
                              )
                            ) {
                              deleteGroup(group.id);
                              toast({
                                title: t("polls:groups_delete_toast_title"),
                                description: t(
                                  "polls:groups_delete_toast_description",
                                  { name: group.name },
                                ),
                              });
                            }
                          }}
                          className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          data-testid="delete-group-button"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Modal d'édition */}
          {editingGroupId && (
            <GroupEditor
              groupId={editingGroupId}
              // `GroupEditor` appelle `useGroups()` de son côté : c'est un
              // état SÉPARÉ de celui de cette page. Une création faite dans la
              // modale n'apparaissait donc qu'après un rechargement manuel.
              // On relit la liste à la fermeture — le coût est une requête,
              // le gain est que l'écran dit la vérité.
              onClose={() => {
                setEditingGroupId(null);
                loadGroups();
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DatePollsGroupsPage;
